<template>
    <Breadcrumb>
        <BreadcrumbList class="max-lg:text-xs">
            <BreadcrumbItem class="group">
                <BreadcrumbLink as-child>
                    <NuxtLink class="flex items-center gap-2" :to="breadcrumbSortedList.first?.to">
                        <Icon
                            v-if="breadcrumbSortedList.first?.icon"
                            :name="breadcrumbSortedList.first.icon.name"
                            :class="twMerge(iconClasses, breadcrumbSortedList.first.iconClasses, breadcrumbSortedList.first.icon.class)"
                        />
                        {{ breadcrumbSortedList.first?.label }}
                    </NuxtLink>
                </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator class="flex">
                <Icon name="majesticons:chevron-left" class="block size-5!" />
            </BreadcrumbSeparator>
            <template v-if="breadcrumbSortedList.menu?.length">
                <BreadcrumbItem class="group">
                    <DropdownMenu>
                        <DropdownMenuTrigger class="flex items-center gap-1">
                            <BreadcrumbEllipsis class="w-4 h-4" />
                            <span class="sr-only">Toggle menu</span>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" class="p-0">
                            <template v-for="(item, key) in breadcrumbSortedList.menu" :key>
                                <DropdownMenuItem class="cursor-pointer" @click="navigateTo(item.to)">
                                    <Icon v-if="item?.icon" :name="item.icon.name" :class="twMerge(iconClasses, item.iconClasses, item.icon.class)" />
                                    {{ item.label }}
                                </DropdownMenuItem>
                            </template>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </BreadcrumbItem>
                <BreadcrumbSeparator class="flex">
                    <Icon name="majesticons:chevron-left" class="block size-5!" />
                </BreadcrumbSeparator>
            </template>
            <template v-if="breadcrumbSortedList.latest?.length">
                <template v-for="(item, key) in breadcrumbSortedList.latest" :key>
                    <BreadcrumbItem class="group">
                        <BreadcrumbLink as-child>
                            <NuxtLink :to="item?.to" class="flex items-center gap-2">
                                <Icon v-if="item?.icon" :name="item.icon.name" :class="twMerge(iconClasses, item.iconClasses, item.icon.class)" />
                                {{ item?.label }}
                            </NuxtLink>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator class="flex">
                        <Icon name="majesticons:chevron-left" class="block size-5!" />
                    </BreadcrumbSeparator>
                </template>
            </template>
            <BreadcrumbItem class="group cursor-default">
                <BreadcrumbPage class="flex items-center gap-2">
                    <Icon
                        v-if="breadcrumbPageItem?.icon"
                        :name="breadcrumbPageItem.icon.name"
                        :class="twMerge(iconClasses, breadcrumbPageItem.iconClasses, breadcrumbPageItem.icon.class)"
                    />
                    {{ breadcrumbPageItem?.label }}
                </BreadcrumbPage>
            </BreadcrumbItem>
        </BreadcrumbList>
    </Breadcrumb>
</template>

<script lang="ts" setup>
import { twMerge } from "tailwind-merge";
import { Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "./";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export interface BreadcrumbItem {
    label: string;
    to?: string;
    iconClasses?: string;
    icon?: {
        name: string;
        class?: string;
    };
}

interface Props {
    list: BreadcrumbItem[];
    maxItems?: number;
}

interface BreadcrumbSortedList {
    first?: BreadcrumbItem;
    latest?: BreadcrumbItem[];
    menu?: BreadcrumbItem[];
}

const props = withDefaults(defineProps<Props>(), {
    maxItems: 3,
});

const iconClasses = "size-3 lg:!size-4 text-neutral-400 group-hover:text-primary";

const breadcrumbPageItem = computed(() => props.list[props.list.length - 1]);

const breadcrumbSortedList = computed<BreadcrumbSortedList>(() => {
    const { list, maxItems } = props;
    const listLength = list.length;
    const overflow = Math.max(listLength - maxItems + 1, 0);

    return {
        first: list[0],
        latest: list.slice(Math.max(listLength - maxItems + 1, 1), -1),
        menu: overflow > 0 ? list.slice(1, listLength - maxItems + 1) : [],
    };
});
</script>
